import { Icon, ThresholdPill } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import styled from 'styled-components';
import { ClearDiv, Col3Col9, FormattedHeaderRow, FormattedTable, FormattedTBodyContainer, FormattedTh } from '../';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import {
	ElementType,
	IDisplayLogicEditModel,
	IPageFlowElementModel,
	ISummaryItem,
	ISurveyElement,
	ISurveyQuestionEditModel,
	SurveyStep,
} from '../../../models';
import { RandomizedItemGroupingHelper } from '../../../utilities';
import { PageBreakRow } from '../../surveys/PageBreakRow';
import { SectionBreakRow } from '../../surveys/SectionBreakRow';
import { SectionHeaderRow } from '../../surveys/SectionHeaderRow';
import { SurveyQuestionRow } from '../../surveys/SurveyQuestionRow';
import { TextElementRow } from '../../surveys/TextElementRow';
import { NoValue } from './NoValue';

export const RandomizedBorder = styled.div`
	border: 1px dashed #bbbbbb;
	border-radius: 2px;
	padding-left: 15px;
	padding-right: 15px;
	padding-top: 15px;
	padding-bottom: 15px;
	margin-bottom: 15px;
`;
RandomizedBorder.displayName = 'RandomizedBorder';

export const RandomizedBorderText = styled.div`
	color: #999999;
	padding-bottom: 15px;
`;

export const QuestionLayoutWrapper = styled.div.attrs<{}>({
	className: 'tws-question-list-summary-item',
})``;
export const QuestionItemLayout = styled.div``;
QuestionItemLayout.displayName = 'QuestionItemLayout';

const LastColumn = styled.div`
	text-align: right;
	margin-right: 10px;
`;

const SummaryFormattedHeaderRow = styled(FormattedHeaderRow)`
	> :nth-child(2) {
		padding-left: 20px !important;
	}
`;
SummaryFormattedHeaderRow.displayName = 'SummaryFormattedHeaderRow';

const FormattedPageFlowElementRow = styled.div.attrs<{}>({
	className: 'tws-formatted-page-flow-element-row',
})`
	margin-bottom: 20px;
	margin-top: 30px;
	margin-right: 0px;
`;
FormattedPageFlowElementRow.displayName = 'FormattedPageFlowElementRow';

export class QuestionsSummaryItem extends React.Component<ISummaryItem> {
	public render() {
		const { displayName, propertyName, value } = this.props;
		const questionSummaryItem = value[0];
		const displayLogicSummaryItem = value[1];
		const questions: ISurveyQuestionEditModel[] =
			questionSummaryItem.value[Constants.QUESTIONS] != undefined ? questionSummaryItem.value[Constants.QUESTIONS] : [];
		const pageFlowElements: IPageFlowElementModel[] =
			questionSummaryItem.value[Constants.PAGE_FLOW_ELEMENTS] != undefined ? questionSummaryItem.value[Constants.PAGE_FLOW_ELEMENTS] : [];
		const elements = (questions as ISurveyElement[]).concat(pageFlowElements);
		elements.sort((e1: ISurveyElement, e2: ISurveyElement) => (e1.sequence || 0) - (e2.sequence || 0));
		const totalPages = elements.filter((e: ISurveyElement) => e.elementType === ElementType.PageBreak).length + 1;
		let pageBreakCount = 1;

		questions.forEach((q) => {
			q.displayLogic = displayLogicSummaryItem.value?.find((dl: IDisplayLogicEditModel) => {
				return dl.displayLogicRule.surveyQuestionId === q.surveyQuestionId!;
			});
		});
		const handleDimensions: boolean = propertyName === Constants.QUESTIONS;
		const questionLimit: number = Constants.QUESTION_LIMIT;

		const elementGroupings = RandomizedItemGroupingHelper.GroupSummarySurveyElements(elements);

		return (
			<QuestionLayoutWrapper>
				<ClearDiv clearHeight={'80px'} />
				<Col3Col9
					label={displayName}
					labelSize='lg'
					subLabelDisplayData={<ThresholdPill count={questions.length} threshold={questionLimit} />}
				>
					<QuestionItemLayout>
						{questions.length > 0 && (
							<FormattedTable paddingLeft={0}>
								<SummaryFormattedHeaderRow>
									<FormattedTh flexBasis={0} />
									<FormattedTh flexBasis={47}>
										{handleDimensions && ContentConstants.TABLE_HEADER_DIMENSIONTEXT}
										{ContentConstants.TABLE_HEADER_QUESTIONTEXT}
									</FormattedTh>
									<FormattedTh flexBasis={18}>{ContentConstants.TABLE_HEADER_RESPONSEOPTION}</FormattedTh>
									<FormattedTh flexBasis={18}>{ContentConstants.TABLE_HEADER_QUESTIONTYPE}</FormattedTh>
									<FormattedTh flexBasis={12}>
										<LastColumn>{ContentConstants.TABLE_HEADER_QUESTION_LOGIC}</LastColumn>
									</FormattedTh>
								</SummaryFormattedHeaderRow>
								<FormattedTBodyContainer>
									<FormattedPageFlowElementRow>
										<PageBreakRow pageNumber={1} totalPages={totalPages} propertyErrors={[]} propertyWarnings={[]} />
									</FormattedPageFlowElementRow>
									{elementGroupings.map((g: ISurveyElement[]) => {
										if (g.length === 1) {
											if (g[0].elementType === ElementType.PageBreak) {
												pageBreakCount = pageBreakCount + 1;
											}
											return this.renderRow(g[0], totalPages, pageBreakCount, handleDimensions);
										} else {
											return (
												<RandomizedBorder key={pageBreakCount}>
													<Icon name='info' />
													<RandomizedBorderText>{ContentConstants.RANDOMIZED_ITEMS_BOX_MESSAGE}</RandomizedBorderText>
													{g.map((e: ISurveyElement) => {
														if (e.elementType === ElementType.PageBreak) {
															pageBreakCount = pageBreakCount + 1;
														}
														return this.renderRow(e, totalPages, pageBreakCount, handleDimensions);
													})}
												</RandomizedBorder>
											);
										}
									})}
								</FormattedTBodyContainer>
							</FormattedTable>
						)}
						{questions.length === 0 && NoValue}
					</QuestionItemLayout>
				</Col3Col9>
			</QuestionLayoutWrapper>
		);
	}

	private renderRow(element: ISurveyElement, totalPages: number, pageBreakCount: number, handleDimensions: boolean) {
		const qidWarnings = this.getWarnings((element as ISurveyQuestionEditModel).surveyQuestionId);
		const eidWarnings = this.getWarnings((element as ISurveyQuestionEditModel).surveyElementId);
		let warnings = [];
		if (qidWarnings != undefined && eidWarnings != undefined) {
			warnings = qidWarnings.concat(eidWarnings);
		} else if (qidWarnings != undefined) {
			warnings = qidWarnings;
		} else if (eidWarnings != undefined) {
			warnings = eidWarnings;
		}

		const qidErrors = this.getErrors((element as ISurveyQuestionEditModel).surveyQuestionId);
		const eidErrors = this.getErrors((element as ISurveyQuestionEditModel).surveyElementId);
		let errors = [];

		switch (element.elementType) {
			case ElementType.PageBreak:
				return (
					<FormattedPageFlowElementRow>
						<PageBreakRow
							totalPages={totalPages}
							pageNumber={pageBreakCount}
							propertyErrors={this.getErrors(element.surveyElementId)}
							propertyWarnings={this.getWarnings(element.surveyElementId)}
							linkToErrors={SurveyStep.LogicAndLayout}
							linkToWarnings={SurveyStep.LogicAndLayout}
						/>
					</FormattedPageFlowElementRow>
				);
			case ElementType.SectionBreak:
				return (
					<FormattedPageFlowElementRow>
						<SectionBreakRow
							propertyErrors={this.getErrors(element.surveyElementId)}
							propertyWarnings={this.getWarnings(element.surveyElementId)}
							linkToErrors={SurveyStep.LogicAndLayout}
							linkToWarnings={SurveyStep.LogicAndLayout}
						/>
					</FormattedPageFlowElementRow>
				);
			case ElementType.SectionHeader:
				return (
					<FormattedPageFlowElementRow>
						<SectionHeaderRow
							propertyErrors={this.getErrors(element.surveyElementId)}
							propertyWarnings={this.getWarnings(element.surveyElementId)}
							linkToErrors={SurveyStep.LogicAndLayout}
							linkToWarnings={SurveyStep.LogicAndLayout}
							text={(element as IPageFlowElementModel).text.textValue}
						/>
					</FormattedPageFlowElementRow>
				);
			case ElementType.TextElement:
				return (
					<FormattedPageFlowElementRow>
						<TextElementRow text={(element as IPageFlowElementModel).text.textValue} />
					</FormattedPageFlowElementRow>
				);
			case ElementType.Question:
				if (qidErrors != undefined && eidErrors != undefined) {
					errors = qidErrors.concat(eidErrors);
				} else if (qidErrors != undefined) {
					errors = qidErrors;
				} else if (eidErrors != undefined) {
					errors = eidErrors;
				}

				return (
					<SurveyQuestionRow
						includeDimension={handleDimensions}
						warnings={warnings}
						errors={errors}
						question={element as ISurveyQuestionEditModel}
						linkToErrors={SurveyStep.LogicAndLayout}
						linkToWarnings={SurveyStep.LogicAndLayout}
						showBranching={false}
						showFollowUpColumn={false}
						isSummary={true}
						isSurveyAdmin={this.props.isSurveyAdmin}
						UpdateAllQuestionTypes={() => {
							/* */
						}}
						openModal={() => {
							/*leaving undefined*/
						}}
						openCustomModal={() => {
							/*leaving undefined*/
						}}
					/>
				);
		}
	}

	private getWarnings(questionId?: number) {
		const questionSummaryItem = this.props.value[0];
		if (
			!questionId ||
			!questionSummaryItem.propertyWarnings ||
			!Object.prototype.hasOwnProperty.call(questionSummaryItem.propertyWarnings, questionId)
		) {
			return [];
		}
		return questionSummaryItem.propertyWarnings[questionId];
	}

	private getErrors(questionId?: number) {
		const questionSummaryItem = this.props.value[0];
		if (
			!questionId ||
			!questionSummaryItem.propertyErrors ||
			!Object.prototype.hasOwnProperty.call(questionSummaryItem.propertyErrors, questionId)
		) {
			return [];
		}
		return questionSummaryItem.propertyErrors[questionId];
	}
}
